import { ROLE_TOKENS } from "../lib/ownership";
import { User, ChatCircleDots, Scales, House } from "@phosphor-icons/react";

const ICON_FOR = {
  staff: User,
  bookkeeper: Scales,        // matches the Scales used in tax-decision header
  owner: House,
};

/**
 * Small inline ownership tag. Use INSIDE a row alongside the row's content.
 * Shows whose turn it is right now.
 *
 *   <OwnerBadge actor="bookkeeper" viewerIsOwn={false} />
 *
 *   actor: "staff" | "bookkeeper" | "owner" | null
 *   viewerIsOwn: when true, the badge is more emphatic ("YOURS" instead of "Yours")
 */
export default function OwnerBadge({ actor, viewerIsOwn = false, compact = false }) {
  if (!actor) return null;
  const tokens = ROLE_TOKENS[actor];
  if (!tokens) return null;
  const Icon = ICON_FOR[actor] || User;
  const text = viewerIsOwn ? "YOURS" : tokens.label;

  return (
    <span
      data-testid={`owner-badge-${actor}${viewerIsOwn ? "-yours" : ""}`}
      className={`inline-flex items-center gap-1 ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]"} font-semibold uppercase tracking-wider`}
      style={{
        color: tokens.color,
        backgroundColor: tokens.soft,
        border: viewerIsOwn ? `1px solid ${tokens.color}` : "1px solid transparent",
      }}
      title={
        actor === "staff"      ? "Business administrator handles this"
      : actor === "bookkeeper" ? "Bookkeeper handles this"
      : actor === "owner"      ? "Owner decides this"
      : ""
      }
    >
      <Icon size={compact ? 9 : 10} weight="duotone" />
      <span className={compact ? "hidden sm:inline" : ""}>{text}</span>
    </span>
  );
}

/**
 * A subtle colored left-edge stripe to apply to a row container.
 * Returns inline style — caller spreads it onto the row's `style`.
 *
 *   <div style={{ ...ownerLeftStripe(actor) }} className="row" />
 */
export function ownerLeftStripe(actor) {
  if (!actor) return {};
  const tokens = ROLE_TOKENS[actor];
  if (!tokens) return {};
  return { boxShadow: `inset 4px 0 0 0 ${tokens.color}` };
}
